import BookFreeClassBtn from '@components/common/book-free-class-btn'
import { FastlyImage } from '@components/common/image'

type WorksheetType = {
  title: string
  copy: string
  border: string
  image: string
}

const WORKSHEETS: WorksheetType[] = [
  {
    title: 'Visual Fill-in',
    copy:
      "The diagram is already there. Your child reads the picture, understands the context, and fills in what's missing, bridging the gap between seeing a concept and writing the answer.",
    border: 'border-violet-100',
    image: '/images/courses/math/activity/visual-fill-in.jpg',
  },
  {
    title: 'Drag & Drop',
    copy:
      'Your child picks up a fraction, a shape, or a number and places it where it belongs. Moving it into position reinforces the relationship in a way that copying answers never could.',
    border: 'border-red-200',
    image: '/images/courses/math/activity/drag-and-drop.jpg',
  },
  {
    title: 'Match the Following',
    copy:
      'Your child draws a line from a problem to its solution, a shape to its property, a fraction to its decimal. Making the connection themselves is what builds the memory.',
    border: 'border-blue-200',
    image: '/images/courses/math/activity/match-the-following.jpg',
  },
  {
    title: 'Rearrange',
    copy:
      'Your child puts the steps or operations into the right order, building an understanding of how a problem works from start to finish.',
    border: 'border-green-100',
    image: '/images/courses/math/activity/rearrange.jpg',
  },
]

const STATS = [
  {
    value: '4-5',
    label: 'Worksheets in every class',
    icon: '/images/courses/math/icons/worksheets.png',
  },
  {
    value: '1-2',
    label: 'Quizzes per session',
    icon: '/images/courses/math/icons/quiz-per-session.png',
  },
  {
    value: '4-5',
    label: 'Homework worksheets after every class',
    icon: '/images/courses/math/icons/homework.png',
  },
  {
    value: '3',
    label: 'Difficulty levels: Easy → Medium → Hard',
    icon: '/images/courses/math/icons/difficulty-levels.png',
  },
]

type Props = {
  urlQuery?: string
}

const InteractiveMathWorksheets = ({ urlQuery }: Props) => {
  return (
    <section className="bg-yellow-100 bg-opacity-25 py-12 lg:py-16 px-5">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-10">
          <h2 className="text-2xl lg:text-4xl font-600 leading-tight mb-4 text-grey max-w-4xl mx-auto">
            Interactive Math worksheets for your child
          </h2>
          <p className="text-base lg:text-lg text-grey max-w-4xl mx-auto">
            Codingal worksheets are built around doing, not just answering. Your
            child engages with Math in multiple ways, which is what builds real
            understanding, not just short-term answers.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 lg:gap-5 mb-10">
          {WORKSHEETS.map((w) => (
            <div
              key={w.title}
              className={`bg-white border-l-4 ${w.border} rounded-xl p-4 lg:p-6 flex items-start gap-4`}
            >
              <div className="min-w-2/6 sm:flex-shrink-0 flex items-center justify-center">
                <FastlyImage
                  src={w.image}
                  alt={w.title}
                  width={149}
                  height={144}
                  className="object-scale-down rounded-lg"
                />
              </div>
              <div className="flex flex-col gap-2">
                <h3 className="text-lg lg:text-xl font-700 text-grey-900">
                  {w.title}
                </h3>
                <p className="text-grey-800 leading-snug">{w.copy}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mb-8">
          {STATS.map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl p-3 space-y-2 border border-grey-300 bg-white"
            >
              <div className="flex items-center justify-start gap-3">
                <FastlyImage
                  src={stat.icon}
                  alt={stat.label}
                  width={45}
                  height={45}
                />
                <p className="text-3xl lg:text-4xl font-700 text-blue-200 leading-none mb-2">
                  {stat.value}
                </p>
              </div>
              <p className="text-sm text-grey-800 leading-snug">{stat.label}</p>
            </div>
          ))}
        </div>

        <div className="bg-orange-100 bg-opacity-40 border border-orange-200 rounded-xl p-3 lg:p-4 flex flex-col sm:flex-row items-center gap-2 sm:gap-4 max-w-4xl mx-auto">
          <FastlyImage
            src="/images/courses/math/icons/cody-with-pencil-circular.png"
            alt="Cody"
            width={60}
            height={60}
            className="flex-shrink-0"
          />
          <p className="lg:text-lg text-orange leading-snug text-center sm:text-left">
            Not too easy. Not too hard. The right challenge, calibrated to where
            your child actually is. That&apos;s the Goldilocks Principle.
          </p>
        </div>

        <div className="flex justify-center mt-8">
          <BookFreeClassBtn query={urlQuery} />
        </div>
      </div>
    </section>
  )
}

export default InteractiveMathWorksheets
